import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { adminApi, mediaUrl } from "../api";
import { AField, Btn, Modal, PageHead, Toggle, Uploader, inputCls } from "../components";

const EMPTY = { name: "", title: "", intro: "", photo: "", visible: true, order: 99 };

export default function TeamAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.get("/admin/team").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = (m) => {
    setForm({ name: m.name, title: m.title || "", intro: m.intro || "", photo: m.photo || "", visible: !!m.visible, order: m.order ?? 99 });
    setEditing(m.id);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, photo: form.photo || null, order: Number(form.order) || 99 };
      if (editing === "new") await adminApi.post("/admin/team", payload);
      else await adminApi.put(`/admin/team/${editing}`, payload);
      setEditing(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const toggleVisible = async (m) => {
    await adminApi.put(`/admin/team/${m.id}`, { name: m.name, title: m.title, intro: m.intro, photo: m.photo, visible: !m.visible, order: m.order });
    load();
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the team?`)) return;
    await adminApi.delete(`/admin/team/${m.id}`);
    load();
  };

  return (
    <div data-testid="admin-team">
      <PageHead title="MEET THE TEAM" sub="Photos, names, titles and order for the public team section.">
        <Btn onClick={() => { setForm(EMPTY); setEditing("new"); }} data-testid="team-new">
          <Plus size={13} /> ADD MEMBER
        </Btn>
      </PageHead>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-list">
        {items.map((m, i) => (
          <div key={m.id} className="border border-woodborder bg-card/40 p-5" data-testid={`team-row-${i}`}>
            <div className="flex items-start gap-4">
              {m.photo ? (
                <img src={mediaUrl(m.photo)} alt={m.name} className="w-16 h-20 object-cover border border-woodborder shrink-0" />
              ) : (
                <div className="w-16 h-20 border border-dashed border-tan/40 bg-wood flex items-center justify-center shrink-0">
                  <span className="font-display text-2xl text-tan/60">{m.name.charAt(0)}</span>
                </div>
              )}
              <div className="min-w-0">
                <p className="font-editorial font-bold text-cream">{m.name}</p>
                <p className="font-mono text-[10px] tracking-[0.12em] text-gold-bright mt-0.5">{m.title}</p>
                <p className="mt-1.5 text-clay text-xs font-light line-clamp-2">{m.intro || "—"}</p>
                <p className="mt-1 font-mono text-[9px] tracking-[0.15em] text-clay">ORDER {m.order} — {m.visible ? "VISIBLE" : "HIDDEN"}</p>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Btn variant="ghost" onClick={() => openEdit(m)} data-testid={`team-edit-${i}`}><Pencil size={12} /> EDIT</Btn>
              <Btn variant="ghost" onClick={() => toggleVisible(m)} data-testid={`team-visible-${i}`}>
                {m.visible ? <EyeOff size={12} /> : <Eye size={12} />} {m.visible ? "HIDE" : "SHOW"}
              </Btn>
              <Btn variant="danger" onClick={() => remove(m)} data-testid={`team-delete-${i}`}><Trash2 size={12} /></Btn>
            </div>
          </div>
        ))}
      </div>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? "ADD MEMBER" : "EDIT MEMBER"}>
        <div className="space-y-5" data-testid="team-editor">
          <AField label="PHOTOGRAPH">
            <div className="flex items-start gap-4 flex-wrap">
              <Uploader onUploaded={(p) => setForm({ ...form, photo: p })} accept="image/*" label="UPLOAD PHOTO" testId="team-photo-upload" />
              {form.photo && <img src={mediaUrl(form.photo)} alt="" className="w-20 h-24 object-cover border border-woodborder" data-testid="team-photo-preview" />}
            </div>
            <p className="mt-2 font-mono text-[9px] tracking-[0.15em] text-clay">REPLACE ANYTIME — NO CODE CHANGES NEEDED.</p>
          </AField>
          <AField label="NAME *"><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name" /></AField>
          <AField label="TITLE"><input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="team-title" /></AField>
          <AField label="SHORT INTRO (OPTIONAL)"><textarea className={`${inputCls} min-h-[80px] resize-y`} value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} data-testid="team-intro" /></AField>
          <div className="grid grid-cols-2 gap-5 items-end">
            <AField label="DISPLAY ORDER"><input type="number" className={inputCls} value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="team-order" /></AField>
            <Toggle checked={form.visible} onChange={(v) => setForm({ ...form, visible: v })} label="VISIBLE" testId="team-visible-toggle" />
          </div>
          <div className="flex gap-3 pt-2 border-t border-woodborder">
            <Btn variant="gold" disabled={saving || !form.name.trim()} onClick={save} data-testid="team-save">{saving ? "SAVING..." : "SAVE MEMBER"}</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
